import {
  ElementRef,
  OnInit,
  Input,
  OnDestroy,
  EventEmitter,
  Output,
  ComponentRef
} from '@angular/core';
import { DesignAttributeType, OptionsAttribute } from '../../../types/designer-attribute-type';
import { DesignerContextType } from '../../../types/designer-context-type';

export class BaseDesignerComponent implements OnInit, OnDestroy {
  // Contexto del designer
  @Input() designerContext: DesignerContextType;
  // Contexto del designer
  @Input() controlContext: any;
  // Tipo de designer
  type: string;
  // Nombre unico de la instancia del designer
  @Input() id: string;
  // Parent designer
  @Input() parent: BaseDesignerComponent;
  // Container
  container: ElementRef;
  // Referencia al componente
  componentRef: ComponentRef<{}>;
  // Atributos en tiempo de diseño
  public attributes: Array<DesignAttributeType>;
  // Indica si es un contenedor (Panel, column, group, etc)
  public isContainer: boolean;
  // Indica si es elegible para el editor de atributos.
  public isSelectable: boolean;
  public childsDesigners: Array<BaseDesignerComponent>;
  public esTipoTabla: boolean;
  public isTypeList: boolean;

  // By default, the configuration is valid, but it can be turned into an invalid
  // configuration via an specific case inside the designer components themselves
  public IsValid = true;

  // Eventos
  @Output() created: EventEmitter<BaseDesignerComponent> = new EventEmitter();
  @Output() destroying: EventEmitter<
    BaseDesignerComponent
  > = new EventEmitter();
  @Output() showMenu: EventEmitter<BaseDesignerComponent> = new EventEmitter();

  constructor(container: ElementRef, type: string) {
    this.childsDesigners = Array<BaseDesignerComponent>();

    this.type = type;
    this.attributes = new Array<DesignAttributeType>();
    this.container = container;
    this.isContainer = false;
    this.isSelectable = true;
  }

  ngOnInit(): void {
    this.container.nativeElement.setAttribute('data-designerType', this.type);
    this.container.nativeElement.setAttribute('data-designerType-id', this.id);

    // Si tengo un contexto cargo el estado del control.
    if (this.controlContext) {
      this.loadState(this.controlContext);

      // Si no hay contexto es un control nuevo y cargo los defaults.
    } else {
      this.loadDefaults();
    }

    // Me agrego a la coleccion de componentes
    if (this.designerContext && this.componentRef) {
      this.designerContext.addDesigner(this.componentRef, this);
    }

    // Me agrego a la coleccion de hijos del padre
    if (this.parent) {
      this.parent.childsDesigners.push(this);
    }

    // El contexto tiene que estar cargado para crear los atributos
    this.buildAttributes();

    // Se emite el evento de creacion del designer
    this.created.emit(this);

    const designerRef = this;

    this.container.nativeElement.addEventListener('click', function(e) {
      const id = designerRef.getId(e.target);

      if (id != null && id === designerRef.id) {
        designerRef.onContainerClick(designerRef);
      }
    });
  }

  // Limpio todo por los leaks
  ngOnDestroy(): void {
    // Aviso que me estan borrando.
    this.destroying.emit(this);

    // Dispose de mis hijos.
    if (this.childsDesigners && this.childsDesigners.length) {
      for (let i = 0; i < this.childsDesigners.length; i++) {
        const childDesigner = this.childsDesigners[i];
        const designerContainer = this.designerContext.designers[
          childDesigner.id
        ];

        if (designerContainer) designerContainer.destroy();
      }
    }

    if (this.parent) {
      // me borro de la coleccion de mi padre
      const index = this.parent.childsDesigners.indexOf(this, 0);
      if (index > -1) {
        this.parent.childsDesigners.splice(index, 1);
      }
    }

    // me borro del contexto
    this.designerContext.removeDesigner(this.id);

    // clean
    this.container.nativeElement.removeEventListener(
      'click',
      this.onContainerClick
    );
    this.componentRef = null;
    this.childsDesigners = null;
    this.designerContext = null;
    this.controlContext = null;
    this.parent = null;
  }

  protected getId(element: any): string {
    let id = null;
    while (element != null && id == null) {
      id = element.getAttribute('data-designerType-id');
      element = element.parentElement;
    }
    return id;
  }

  // Devuelve el ID del proximo designer
  protected getSiblingDesigner(element: any): string {
    let elementId = null;
    let siblingId = null;
    // Encuentro el id del elemento
    while (element != null && elementId == null) {
      elementId = element.getAttribute('data-designerType-id');
      element = element.parentElement;
    }

    // Recorro los hijos del padre hasta encontrar el proximo designer.
    if (element) {
      for (let i = 0; i < element.children.length; i++) {
        const childId = element.children[i].getAttribute(
          'data-designerType-id'
        );

        if (childId && childId !== elementId) {
          siblingId = childId;
          break;
        }
      }
    }

    return siblingId;
  }

  // Retorna los atributos en tiempo de diseño
  protected buildAttributes(): void {
    if (this.type !== 'SxViewEngine') {
      this.attributes.push(
        new DesignAttributeType(
          this.controlContext,
          'string',
          'tooltip',
          'TOOLTIP'
        )
      );
      this.attributes.push(
        new DesignAttributeType(
          this.controlContext,
          'string',
          'category',
          'CATEGORY'
        )
      );
      this.attributes.push(
        new DesignAttributeType(
          this.controlContext,
          'boolean',
          'hidden',
          'HIDDEN'
        )
      );

      const dependenciesAtt = new DesignAttributeType(
        this.controlContext,
        'string',
        'dependencies',
        'DEPENDENCIES'
      );
      const dependenciesJS = new DesignAttributeType(
        this.controlContext,
        'string',
        'dependenciesJS',
        'DEPENDENCIES_JS'
      );
      const dependenciesCustomErrorJS = new DesignAttributeType(
        this.controlContext,
        'string',
        'dependenciesCustomErrorJS',
        'DEPENDENCIES_CUSTOM_ERROR_JS'
      );
      const customErrorMessage = new DesignAttributeType(
        this.controlContext,
        'string',
        'customErrorMessage',
        'CUSTOM_ERROR_MESS'
      );
      customErrorMessage.hiddenConditions = [[dependenciesCustomErrorJS.name, '==', '']];
      customErrorMessage.hiddenConditions = [[dependenciesCustomErrorJS.name, '==', undefined]];

      const behaviourOptions = [
        { id: 'hidden', name: 'Hidden' },
        { id: 'disabled', name: 'Disabled' }
      ];
      const dependencyBehaviour = new OptionsAttribute(
        this.controlContext,
        behaviourOptions,
        'dependencyBehaviour',
        'BEHAVIOURS'
      );
    
      this.attributes.push(
        dependenciesAtt,
        dependenciesJS,
        dependencyBehaviour,
        dependenciesCustomErrorJS,
        customErrorMessage);
    }
  }

  // Carga la info de un designer para recostruir las propiedades.
  loadState(jsonRoot: any) {
    if (jsonRoot == null) {
      return;
    }

    this.id = jsonRoot['id'];
    this.controlContext.dynamicStyle = jsonRoot['dynamicStyle'];
  }

  // Carga el default de los controles.
  loadDefaults(): void {
    // Inicializo el contexto en vacio.
    this.controlContext = {};
      if (this.type !== 'SxViewEngine') {
        this.controlContext.tooltip = '';
        this.controlContext.hidden = false;
        this.controlContext.category = '';
        this.controlContext.dynamicStyle = {};
      }
    }

  // Retorna el JSON del designer, es lo que se guarda en base de datos.
  getDesignerInfo(): any {
    const info = {};

    info['type'] = this.type;
    info['id'] = this.id;
    if (this.type !== 'SxViewEngine') {
      if (this.controlContext && this.controlContext.tooltip) {
        info['tooltip'] = this.controlContext.tooltip;
      } else {
        info['tooltip'] = '';
      }
      for (let i = 0; i < this.attributes.length; i++) {
        const attribute = this.attributes[i];

        info[attribute.name] = attribute.value;
      }
      info['hidden'] = this.controlContext.hidden;

      info['category'] = this.controlContext.category;

      info['dynamicStyle'] = this.controlContext.dynamicStyle;

      info['dependencies'] = this.controlContext.dependencies;
      info['dependenciesJS'] = this.controlContext.dependenciesJS;
      info['dependencyBehaviour'] = this.controlContext.dependencyBehaviour;
      info['dependenciesCustomErrorJS'] = this.controlContext.dependenciesCustomErrorJS;
      info['customErrorMessage'] = this.controlContext.customErrorMessage;
    }
    return info;
  }

  onContainerClick(designerRef): void {
    // Lanzo el evento para que el designer muestre el menu
    if (this.isSelectable) {
      designerRef.showMenu.emit(designerRef);
    }
  }

  // gets the object reference to a designer element so it can
  // be modified at start
  getReferenceById(searchId: string): BaseDesignerComponent {
    let ret = null;
    if (this.id === searchId) {
      return this;
    } else {
      if (this.childsDesigners.length === 0) {
        return null;
      } else {
        let i = 0;
        for (i = 0; i < this.childsDesigners.length; i++) {
          ret = this.childsDesigners[i].getReferenceById(searchId);
          if (ret !== null) {
            return ret;
          }
        }
      }
    }
    return ret;
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/sxfdesigner/components/designers/base/base-designer-component.ts